/* istanbul ignore file */
import { useState } from 'react';
import { useAuthorization } from 'framework/authorization';
import { Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import {
  RECRUITING_VACANCY_REQUEST_CANCELLATION,
  RECRUITING_VACANCY_REQUEST_CHANGE,
  VACANCY_CANCEL_REASON_MAX_LENGTH,
  VACANCY_STATUS,
  useVacancyGroupService,
} from 'domain/shared/newVacancy';
import { useFieldHelper } from 'framework/ui/field';
import { SelectInput, TextArea } from 'framework/form';
import { useShowContext } from 'framework/show';
import { RECRUITING_CONTEXT } from '../../../RecruitingContext';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';
import { Box, Typography } from '@mui/material';

const REQUEST_TYPES = {
  CANCELLATION: 'request_cancellation',
  CHANGE: 'change_request',
} as const;

const RequestVacancyCancellationButton = ({
  statusSource,
  setGeneralContextData,
}: {
  statusSource: string;
  setGeneralContextData: () => {};
}) => {
  const { requestVacancyCancellation, requestVacancyChange } = useVacancyGroupService();
  const { isGranted } = useAuthorization();
  const { addMessage } = useMessageService();

  const { data } = useShowContext();
  const { id } = data.getData();

  const [modalOpen, setModalOpen] = useState(false);
  const hasChangePermission = isGranted(RECRUITING_VACANCY_REQUEST_CHANGE, RECRUITING_CONTEXT);
  const hasCancellationPermission = isGranted(RECRUITING_VACANCY_REQUEST_CANCELLATION, RECRUITING_CONTEXT);

  // Set initial request type based on permissions
  const [requestType, setRequestType] = useState(
    hasChangePermission ? REQUEST_TYPES.CHANGE : REQUEST_TYPES.CANCELLATION,
  );
  const [reason, setReason] = useState<string>('');
  const [reasonErrors, setReasonErrors] = useState<string[]>([]);

  const { getValue } = useFieldHelper({ source: statusSource });
  const status = getValue();

  if (status !== VACANCY_STATUS.OPEN || (!hasChangePermission && !hasCancellationPermission)) {
    return null;
  }

  const closeModal = () => {
    setModalOpen(false);
    setReason('');
    setReasonErrors([]);
  };

  const getReasonErrors = (newReason: string, type: string) => {
    if (!newReason) {
      return ['Reason should be provided'];
    }

    if (type === REQUEST_TYPES.CANCELLATION && newReason.length > VACANCY_CANCEL_REASON_MAX_LENGTH) {
      return [`Cancellation reason max length is ${VACANCY_CANCEL_REASON_MAX_LENGTH}`];
    }

    return [];
  };

  const onReasonChange = (values: any) => {
    const newReason = values?.reason || '';
    setReason(newReason);
    setReasonErrors(getReasonErrors(newReason, requestType));
  };

  const onRequestTypeChange = (values: any) => {
    setRequestType(values.requestType);
    setReason('');
    setReasonErrors([]);
  };

  const getModalTitle = () => {
    return requestType === REQUEST_TYPES.CANCELLATION ? 'Request vacancy cancellation' : 'Request vacancy change';
  };

  const getReasonLabel = () => {
    return requestType === REQUEST_TYPES.CANCELLATION ? 'Cancellation reason' : 'Change request reason';
  };

  const getButtonLabel = () => {
    if (hasChangePermission && hasCancellationPermission) {
      return 'Change request';
    }
    return hasChangePermission ? 'Change request' : 'Request cancellation';
  };

  const handleSubmit = async () => {
    const errors = getReasonErrors(reason, requestType);
    if (errors.length) {
      setReasonErrors(errors);
      return;
    }

    try {
      if (requestType === REQUEST_TYPES.CANCELLATION) {
        await requestVacancyCancellation(id!, reason);
        addMessage(new SuccessMessage('Cancellation for vacancy has been successfully requested.'));
      } else {
        await requestVacancyChange(id!, reason);
        addMessage(new SuccessMessage('Change for vacancy has been successfully requested.'));
      }

      if (setGeneralContextData) {
        setGeneralContextData();
      }

      closeModal();
    } catch (error) {
      addMessage(
        new ErrorMessage(
          requestType === REQUEST_TYPES.CANCELLATION
            ? "Can't request vacancy cancellation."
            : "Can't request vacancy change.",
        ),
      );
    }
  };

  const selectOptions = [
    { value: REQUEST_TYPES.CHANGE, label: 'Change request' },
    { value: REQUEST_TYPES.CANCELLATION, label: 'Request cancellation' },
  ];

  const confirmButton = (
    <Button
      key="confirmBtn"
      onClick={handleSubmit}
      color={requestType === REQUEST_TYPES.CANCELLATION ? 'error' : 'primary'}
      disabled={!reason || reasonErrors.length > 0}
    >
      {requestType === REQUEST_TYPES.CANCELLATION ? 'Request cancellation' : 'Request change'}
    </Button>
  );

  return (
    <>
      <Button onClick={() => setModalOpen(true)} color={!hasChangePermission ? 'error' : 'primary'} variant="outlined">
        {getButtonLabel()}
      </Button>

      <Modal backdrop="static" isOpen={modalOpen} title={getModalTitle()} onToggle={closeModal} buttons={confirmButton}>
        {hasChangePermission && hasCancellationPermission && (
          <Box sx={{ mb: 3 }}>
            <SelectInput
              source="requestType"
              label="Request type"
              options={selectOptions}
              value={requestType}
              onChangeCallback={onRequestTypeChange}
            />
          </Box>
        )}

        <Box>
          <Typography variant="body1" mb={1}>
            {requestType === REQUEST_TYPES.CANCELLATION
              ? 'Do you really want to request cancellation for this vacancy? Please describe below.'
              : 'Please describe the changes needed for this vacancy.'}
          </Typography>

          <TextArea
            key={`textarea-${requestType}`}
            source="reason"
            label={getReasonLabel()}
            value={reason}
            onChangeCallback={onReasonChange}
            errorMessages={reasonErrors}
            isRequired
          />
        </Box>
      </Modal>
    </>
  );
};

export default RequestVacancyCancellationButton;
